import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { _ } from '@splunk/ui-utils/i18n';
import Eye from '@splunk/react-icons/Eye';
import EyeSlashed from '@splunk/react-icons/EyeSlashed';
import Tooltip from '@splunk/react-ui/Tooltip';
import Button from '@splunk/react-ui/Button';
import { variables } from '@splunk/themes';
import type { ShowHideState } from '@splunk/dashboard-types';

const StyledTooltip = styled(Tooltip)`
    [type='toggle-show'] > &,
    [type='toggle-hide'] > & {
        margin-bottom: 0;
        display: flex;
        justify-content: center;
    }
`;

const StyledButton = styled(Button)`
    width: ${variables.inputHeight};
    flex-grow: 0;
`;

const showIcon = (
    <StyledTooltip content={_('Show when condition is met')} inline={false}>
        <Eye width="16px" height="16px" />
    </StyledTooltip>
);

const hideIcon = (
    <StyledTooltip content={_('Hide when condition is met')} inline={false}>
        <EyeSlashed width="16px" height="16px" />
    </StyledTooltip>
);

type OnToggleState = (newState: ShowHideState) => void;

export const ShowHideEye = ({
    state,
    onToggleState,
}: {
    state: ShowHideState;
    onToggleState: OnToggleState;
}) => {
    const stateRef = useRef(state);
    stateRef.current = state;

    const onToggle = useCallback(() => {
        onToggleState(stateRef.current === 'show' ? 'hide' : 'show');
    }, [onToggleState]);

    return (
        <StyledButton
            data-test={`item-toggle-${state === 'show' ? 'hide' : 'show'}`}
            appearance="subtle"
            icon={state === 'show' ? showIcon : hideIcon}
            onClick={onToggle}
            data-test-state={state}
            role="switch"
            aria-checked={state === 'show'}
            aria-label={_('Toggle visibility condition')}
        />
    );
};
